import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL-backed view state.
 *
 * DESIGN-CONCEPT §3.2: active tab, filters, search text, range and page number
 * live in query params, and "local component state for anything filter-shaped is
 * a defect". The reason is not tidiness — it is that an ops flow is
 * list → record → back, many times an hour, and with the filters in component
 * state that round trip resets them every time. It also makes a view sendable:
 * «this one, look» is a link.
 *
 * A schema of `{ param: { default, values? , parse?, serialise? } }` gives three
 * things a bare `useSearchParams` does not:
 *
 *   • DEFAULTS ARE ABSENT FROM THE URL. A param at its default is deleted rather
 *     than written, so the common view has a clean address and two operators who
 *     arrived differently share one.
 *   • UNKNOWN VALUES FALL BACK. `?status=BANANA` reads as the default instead of
 *     filtering everything away — a stale bookmark or a hand-edited address must
 *     not produce an empty screen with no explanation.
 *   • `replace` BY DEFAULT. Typing in a search box must not push twenty history
 *     entries, or Back becomes unusable exactly when it is needed.
 *
 * Values are always ASCII in the URL (§16.1): the digits an operator sees are a
 * rendering, and a Persian digit in an address is a link that breaks when
 * pasted.
 */
export function useUrlState(schema) {
  const [params, setParams] = useSearchParams();

  const state = useMemo(() => {
    const out = {};
    for (const [key, spec] of Object.entries(schema)) {
      const raw = params.get(key);
      if (raw == null || raw === '') { out[key] = spec.default; continue; }
      const parsed = spec.parse ? spec.parse(raw) : raw;
      // A value outside the allowed set is junk, not a filter. Falling back beats
      // rendering an empty list the operator cannot explain.
      const ok = spec.values ? spec.values.includes(parsed) : parsed != null;
      out[key] = ok ? parsed : spec.default;
    }
    return out;
  }, [params, schema]);

  /**
   * Merge a patch into the URL. `null`/`undefined`/'' clears a param, as does
   * setting it to its default.
   */
  const update = useCallback((patch, { push = false } = {}) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(patch)) {
        const spec = schema[key] || {};
        const serialised = value == null ? '' : String(spec.serialise ? spec.serialise(value) : value);
        if (serialised === '' || serialised === String(spec.default ?? '')) next.delete(key);
        else next.set(key, serialised);
      }
      return next;
    }, { replace: !push });
  }, [setParams, schema]);

  /** Clear every param in the schema. Used by «حذف فیلترها». */
  const reset = useCallback(() => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const key of Object.keys(schema)) next.delete(key);
      return next;
    }, { replace: true });
  }, [setParams, schema]);

  return [state, update, reset];
}

export default useUrlState;
